import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SocialShare from './components/SocialShare';

const questions = [
  {
    id: 1,
    question: "I enjoy meeting new people while traveling.",
    trait: "extraversion"
  },
  {
    id: 2,
    question: "I like to have a detailed plan for my trips.",
    trait: "conscientiousness"
  },
  {
    id: 3,
    question: "I am open to trying new foods and experiences.",
    trait: "openness"
  },
  {
    id: 4,
    question: "I stay calm and relaxed even when things go wrong on a trip.",
    trait: "emotional_stability"
  },
  {
    id: 5,
    question: "I am considerate and cooperative with travel companions.",
    trait: "agreeableness"
  }
];

const traitLabels = {
  extraversion: "Extraversion",
  conscientiousness: "Conscientiousness",
  openness: "Openness to Experience",
  emotional_stability: "Emotional Stability",
  agreeableness: "Agreeableness"
};

export default function PersonalityQuiz() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();

  const handleSlider = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: Number(value)
    }));
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const resetQuiz = () => {
    setAnswers({});
    setCurrentQuestion(0);
    setShowResults(false);
  };

  const getProgress = () => {
    return ((currentQuestion + 1) / questions.length) * 100;
  };

  // Calculate a simple percentage match (average of all slider values, scaled to 100%)
  const getPercentageMatch = () => {
    const total = Object.values(answers).reduce((sum, v) => sum + v, 0);
    const max = questions.length * 5;
    return Math.round((total / max) * 100);
  };

  if (showResults) {
    const percent = getPercentageMatch();
    return (
      <div>
        <section className="bg-light p-6">
          <div className="container">
            <div className="text-center">
              <h1>Your Personality Match</h1>
              <p className="text-muted">
                This is your overall compatibility score based on your answers.
              </p>
            </div>
          </div>
        </section>
        <section className="p-6">
          <div className="container">
            <div className="row">
              <div className="col col--12 text-center">
                <h2 style={{ fontSize: '2.5em', color: '#3b82f6', margin: '0.5em 0' }}>{percent}%</h2>
                <p style={{ fontSize: '1.2em', marginBottom: '1.5em' }}>Share your result and invite friends to find their perfect travel match!</p>
                <SocialShare
                  url={window.location.href}
                  title={`I scored ${percent}% on the GradedWash Personality Quiz!`}
                  summary={`Find your perfect travel companion with GradedWash. Take the quiz now!`}
                  hashtag="#PersonalityQuiz"
                />
              </div>
            </div>
            <div className="row">
              <div className="col col--8" style={{ margin: '0 auto' }}>
                <div className="card p-6 text-center">
                  <div className="bg-primary text-white" style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto var(--spacing-6)',
                    fontSize: 'var(--font-size-3xl)'
                  }}>
                    {percent}%
                  </div>
                  <h2 style={{ marginBottom: 'var(--spacing-4)' }}>{percent}% Match</h2>
                  <p style={{ fontSize: 'var(--font-size-lg)', lineHeight: 1.6, marginBottom: 'var(--spacing-6)' }}>
                    This score reflects how your personality aligns with our ideal travel companion profile. You'll be matched with others who have similar scores and traits.
                  </p>
                  <div className="row">
                    <div className="col col--12">
                      <h4>Your Trait Scores</h4>
                      <ul style={{ textAlign: 'left', listStyle: 'none', padding: 0 }}>
                        {questions.map(q => (
                          <li key={q.id} style={{ marginBottom: 'var(--spacing-2)' }}>
                            <strong>{traitLabels[q.trait]}</strong>: <span className="text-muted">{answers[q.id] || 0}/5</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="d-flex justify-center" style={{ gap: 'var(--spacing-4)', marginTop: 'var(--spacing-6)' }}>
                    <button onClick={resetQuiz} className="btn btn--secondary">Retake Quiz</button>
                    <button onClick={() => navigate('/profile')} className="btn btn--primary">Go to Profile</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const q = questions[currentQuestion];
  return (
    <div>
      <section className="bg-light p-6">
        <div className="container">
          <div className="text-center">
            <h1>Personality Test</h1>
            <p className="text-muted">Slide to answer each question. Your answers help us match you with compatible travel companions.</p>
          </div>
        </div>
      </section>
      <section className="p-6">
        <div className="container">
          <div className="row">
            <div className="col col--8" style={{ margin: '0 auto' }}>
              <div className="card p-6">
                <div style={{ marginBottom: 'var(--spacing-6)' }}>
                  <div style={{ fontWeight: 'bold', fontSize: 'var(--font-size-lg)' }}>
                    Question {currentQuestion + 1} of {questions.length}
                  </div>
                  <div className="progress-bar" style={{ width: '100%', height: '8px', background: '#eee', borderRadius: '4px', margin: '16px 0' }}>
                    <div style={{ width: `${getProgress()}%`, height: '100%', background: 'var(--primary-blue)', borderRadius: '4px' }} />
                  </div>
                </div>
                <h2 style={{ marginBottom: 'var(--spacing-6)' }}>{q.question}</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: 'var(--spacing-6)' }}>
                  <span style={{ minWidth: 80, color: '#888' }}>Strongly Disagree</span>
                  <input
                    type="range"
                    min={1}
                    max={5}
                    step={1}
                    value={answers[q.id] || 3}
                    onChange={e => handleSlider(q.id, e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <span style={{ minWidth: 80, color: '#888' }}>Strongly Agree</span>
                </div>
                <div className="d-flex justify-between" style={{ marginTop: 'var(--spacing-4)' }}>
                  <button onClick={prevQuestion} className="btn btn--secondary" disabled={currentQuestion === 0}>Previous</button>
                  <button onClick={nextQuestion} className="btn btn--primary">
                    {currentQuestion === questions.length - 1 ? 'See Results' : 'Next'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

